
import { defineComponent, onMounted, ref, watch, computed } from 'vue'
import BarChart from '@/components/Charts/BarChart.vue'
import Pagination from '@/components/Pagination.vue'
import { getKnowledgeModules } from '@/graphql-operations/getKnowledgeData'
import { useQuery } from '@vue/apollo-composable'
import { AttensiLoadingSpinner } from '@attensi/materia'
import ReportDownloadButton from '@/components/ReportDownloadButton.vue'
import { useI18n } from 'vue-i18n'
import * as Sentry from '@sentry/vue'

export default defineComponent({
  components: {
    BarChart,
    Pagination,
    AttensiLoadingSpinner,
    ReportDownloadButton,
  },
  props: {
    report: {
      required: true,
      type: Object as any,
    },
  },
  emits: ['show-no-data-modal'],
  setup(props, { emit }) {
    const { t } = useI18n()

    const queryData = ref<any>(null)
    const loadingData = ref()
    const pageSize = 15
    const totalPageCount = ref<number>(0)
    const currentPage = ref<number>(1)
    const itemsPerPage = ref<number>(15)
    const queriesData = ref<any>()
    const dataType = ref<string>('Products')
    const isAllFiltersNotSelected = ref<boolean>(false)
    const selectedIndex = ref(0)

    const productIds = computed(() =>
      props.report.selectedProducts
        ? props.report.selectedProducts.map((p: any) => p.id)
        : undefined
    )
    const moduleIds = computed(() =>
      props.report.selectedModules
        ? props.report.selectedModules.map((m: any) => m.id)
        : undefined
    )

    if (!moduleIds.value && !productIds.value) {
      isAllFiltersNotSelected.value = true
    }

    const loadData = async (start = 0) => {
      queriesData.value = await getKnowledgeModules(
        start,
        productIds.value,
        moduleIds.value
      )

      const { result, error, loading } = useQuery(
        queriesData.value.query,
        queriesData.value.variables
      )

      watch([result, error, loading, dataType], async () => {
        if (error.value) {
          Sentry.captureException({ error: error.value })
          return
        }

        if (result.value.knowledge.length === 0) {
          emit('show-no-data-modal', true)
          return
        }

        if (result.value) {
          totalPageCount.value = Math.ceil(
            result.value.knowledge_aggregate.aggregate.count
          )

          const percentageFirstAttempt =
            result.value.knowledge.length !== 0
              ? result.value.knowledge.map(
                  (pca: any) => pca.percentage_first_attempt ?? 0
                )
              : []
          const percentageKnowledgeGapClosed =
            result.value.knowledge.length !== 0
              ? result.value.knowledge.map(
                  (pkgc: any) => pkgc.percentage_knowledge_gap_closed ?? 0
                )
              : []
          const remainingKnowledgeGap =
            result.value.knowledge.length !== 0
              ? result.value.knowledge.map(
                  (pkgc: any) => pkgc.remaining_knowledge_gap ?? 0
                )
              : []

          const categories = result.value.knowledge.map(
            (pn: any) => pn.module_name
          )

          queryData.value = {
            categories,
            series: [
              {
                name: 'Percentage First Attempt',
                color: '#5D5D5D',
                data: percentageFirstAttempt,
              },
              {
                name: 'Percentage Knowledge Gap Closed',
                color: '#C469E3',
                data: percentageKnowledgeGapClosed,
              },
              {
                name: 'Remaining Knowledge Gap',
                color: '#E7B3F8',
                data: remainingKnowledgeGap,
              },
            ],
          }
        }
      })
    }

    const changePage = async (pageNumber: number) => {
      const start = (pageNumber - 1) * pageSize
      currentPage.value = pageNumber
      queryData.value = await loadData(start)
    }

    const changeType = async (type: string, index: number) => {
      dataType.value = type
      currentPage.value = 1
      selectedIndex.value = index
      queryData.value = await loadData(0)
    }

    onMounted(loadData)

    return {
      queryData,
      loadingData,
      totalPageCount,
      currentPage,
      itemsPerPage,
      changePage,
      loadData,
      dataType,
      changeType,
      selectedIndex,
      t,
    }
  },
})
